/**
 * The decisions-API client: one `fetch`, one Zod-validated response, no SDK.
 *
 * A decisions model is not reached through chat completions — it has its own
 * endpoint, which takes `state` plus a `questions` map and answers with one typed
 * answer per question. `schemaToQuestions` in `decision-questions.ts` builds the
 * map and `answersToObject` folds the answers back, so this module's whole job is
 * the hop between them: send, retry the two overload statuses, and either hand
 * back answers this shape recognises or hand back nothing.
 *
 * **Failing closed is the contract.** Every way a response can fail to be a
 * decisions response — transport, a status this client does not retry past, a
 * body that is not JSON, and a body whose shape this module's schema refuses —
 * resolves to `{ ok: false }` with a reason. There is no partial result: an
 * `answers` map with one unrecognised answer refuses the whole response, because
 * a caller that acted on the recognised half would be acting on a reading of a
 * response it could not read. The caller then takes whatever path it takes for a
 * completion it could not parse.
 *
 * Credentials arrive as `apiKey` and nothing here reads a store: a backend caller
 * resolves the connection's key through `resolveConnectionSecret` and passes it
 * in.
 */

import { z } from 'zod';
import type { DecisionAnswer, DecisionQuestion } from './decision-questions';

/**
 * The endpoint a connector's decisions calls go to.
 *
 * OpenRouter's Decisions endpoint is not under its `/api/v1` chat surface — the
 * path is `/api/alpha/decisions` on the same host. A direct TypeSafe connector
 * would add its own endpoint constant beside this one and pass it in the same
 * field.
 */
export const OPENROUTER_DECISIONS_ENDPOINT = 'https://openrouter.ai/api/alpha/decisions';

/**
 * The two statuses both vendors document as retry-with-backoff: 429 for a rate
 * limit and 529 for an overloaded provider.
 *
 * Every other non-2xx fails the call closed rather than being retried. That is a
 * narrower set than `RETRYABLE_LLM_PROVIDER_STATUSES` in `errors.ts`, which
 * answers a different question — whether a chat completion is worth another
 * attempt — and this client would rather return no output than spend a
 * synchronous monitor's latency budget on a status neither vendor says to retry.
 */
const RETRYABLE_STATUSES: readonly number[] = [429, 529];

/** Attempts in total, including the first, when the caller sets no bound. */
export const DECISIONS_DEFAULT_ATTEMPTS = 3;
/** First backoff step; each further step doubles it. */
export const DECISIONS_DEFAULT_BACKOFF_MS = 200;
/** Ceiling on one backoff step, including one honoured from `Retry-After`. */
export const DECISIONS_MAX_BACKOFF_MS = 2000;
/** Per-attempt wall-clock bound, spanning the headers AND the body read. */
export const DECISIONS_DEFAULT_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

export interface DecisionsRequest {
  /** The connector's endpoint — `OPENROUTER_DECISIONS_ENDPOINT` today. */
  endpoint: string;
  /** Resolved through the connection-secret resolver by the caller; never read here. */
  apiKey: string;
  model: string;
  /** What the questions are asked about. */
  state: string | Record<string, unknown> | unknown[];
  questions: Record<string, DecisionQuestion>;
  /**
   * Request-grouping id — the conversation id. An ID and never text: the field is
   * sent to the provider, and a decisions call's state is the only place
   * conversation content belongs.
   */
  sessionId?: string | null;
  /** End-user id — the hub id. An ID and never text, for the same reason. */
  user?: string | null;
  /**
   * The connection's provider preferences, forwarded unchanged. They are the
   * customer's routing and data-handling choices on their own account, so this
   * client neither supplies nor edits them.
   */
  providerPreferences?: Record<string, unknown> | null;
}

export interface DecisionsClientOptions {
  /** Injected so a test drives the wire; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Injected for the same reason; defaults to a timer. */
  sleep?: (ms: number) => Promise<void>;
  attempts?: number;
  backoffMs?: number;
  timeoutMs?: number;
}

// ---------------------------------------------------------------------------
// Response
// ---------------------------------------------------------------------------

/**
 * One answer, carrying exactly the fields `answersToObject` reads.
 *
 * A choice and a score answer also carry `probabilities`, and a score a `legend`.
 * They are not declared: an object schema strips what it does not declare, so
 * they neither refuse a response nor reach a caller that has no type for them.
 * Declaring one is the change that adds its reader.
 *
 * `noul` declares no confidence because the API sends none — its 0-to-1 value IS
 * the probability, which is why `answersToObject` derives a noul's confidence
 * from it rather than reading a field.
 */
const decisionAnswerSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('choice'),
    choice: z.string(),
    confidence: z.number().optional(),
  }),
  z.object({
    type: z.literal('score'),
    score: z.number(),
    confidence: z.number().optional(),
  }),
  z.object({
    type: z.literal('noul'),
    noul: z.number(),
  }),
]);

/**
 * The response this client recognises.
 *
 * Unknown TOP-LEVEL keys are stripped rather than refused, so a field the
 * endpoint adds does not take the whole call down. An unknown ANSWER is refused,
 * because an answer is what the caller acts on and a type this module cannot
 * decode is a decision it cannot read.
 */
const decisionsResponseSchema = z.object({
  id: z.string().optional(),
  model: z.string().optional(),
  provider: z.string().optional(),
  answers: z.record(decisionAnswerSchema),
  usage: z
    .object({
      input_tokens: z.number().optional(),
      output_tokens: z.number().optional(),
      cost: z.number().optional(),
    })
    .optional(),
});

export interface DecisionsUsage {
  input_tokens: number | null;
  output_tokens: number | null;
  /**
   * What the provider charged, in USD. Preferred over catalog pricing by the
   * caller that records cost: the response prices the call that happened, while
   * the catalog prices the model this client asked for.
   */
  cost: number | null;
}

/**
 * Why a call produced no output.
 *
 * `transport` — the request did not deliver a body, for a reason that is not
 *   this attempt's own abort: a refused connection or a DNS failure before the
 *   headers, and a read that FAILED after them (the peer reset the connection, a
 *   proxy truncated the stream). `status` is null in the first case and the
 *   answered status in the second — a 2xx header commits nothing about a body
 *   that never arrived.
 * `timeout` — this attempt's abort fired. It covers BOTH ends of the attempt,
 *   because the timeout spans the headers AND the body read: either the provider
 *   never answered, or it answered a header and then stalled. `status` is null in
 *   the first case and the answered status in the second, and in neither does it
 *   mean the provider sent something usable.
 * `http_status` — a non-2xx this client does not retry, or a retryable one whose
 *   attempts ran out; `status` carries it.
 * `unparseable_body` — the bytes ARRIVED and are not JSON. Narrowly that, and
 *   nothing else: a read cut short by the abort is `timeout`, and one cut short
 *   by the peer is `transport`.
 * `unexpected_shape` — JSON the response schema refuses.
 *
 * **A 2xx is not a promise about the body, so the reason is read from the cause
 * rather than the status.** Four different things can go wrong after headers
 * that say 200 — a stall, a reset, malformed bytes, a shape this client refuses
 * — and only the last two are the provider answering something. A caller that
 * MAPS these onto a backend's provider-error taxonomy takes a different path for
 * each: reschedule a stall, fail a reset, record an unreadable answer and move
 * on. The status is 200 for all four and so can separate none of them.
 */
export type DecisionsFailureReason =
  | 'transport'
  | 'timeout'
  | 'http_status'
  | 'unparseable_body'
  | 'unexpected_shape';

export type DecisionsResult =
  | {
      ok: true;
      /** Feed straight to `answersToObject` with the agent's own schema. */
      answers: Record<string, DecisionAnswer>;
      /** What the provider says it ran, which need not be the id that was asked for. */
      model: string | null;
      provider: string | null;
      id: string | null;
      usage: DecisionsUsage;
    }
  | { ok: false; reason: DecisionsFailureReason; status: number | null };

// ---------------------------------------------------------------------------
// The call
// ---------------------------------------------------------------------------

/**
 * Ask a decisions model. Never throws and never returns a half-read response.
 */
export async function requestDecisions(
  request: DecisionsRequest,
  options: DecisionsClientOptions = {},
): Promise<DecisionsResult> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const sleep = options.sleep ?? defaultSleep;
  const attempts = Math.max(1, options.attempts ?? DECISIONS_DEFAULT_ATTEMPTS);
  const backoffMs = Math.max(0, options.backoffMs ?? DECISIONS_DEFAULT_BACKOFF_MS);
  const timeoutMs = Math.max(1, options.timeoutMs ?? DECISIONS_DEFAULT_TIMEOUT_MS);

  const body = JSON.stringify(buildPayload(request));

  for (let attempt = 0; attempt < attempts; attempt++) {
    // `AbortController` plus a timer rather than `AbortSignal.timeout`, because
    // this package is RN-safe. The timer is cleared only once the attempt is
    // DONE — a fetch resolves when the headers land, and the body is read after
    // that, so releasing the signal any earlier leaves the body read unbounded
    // and a provider that answers a header and then stalls hangs the call.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let retryDelayMs: number | null = null;
    try {
      let response: Response;
      try {
        response = await fetchImpl(request.endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${request.apiKey}`,
            'Content-Type': 'application/json',
          },
          body,
          signal: controller.signal,
        });
      } catch {
        // A rejected fetch is this attempt's own abort or a genuine transport
        // fault, and the signal is what tells them apart — the rejection itself
        // does not, since an abort surfaces as an ordinary rejection here.
        // Neither says the next attempt would fail, but retrying a call that may
        // already have run is a second charge for one decision.
        return {
          ok: false,
          reason: controller.signal.aborted ? 'timeout' : 'transport',
          status: null,
        };
      }

      if (!response.ok) {
        // workerd cannot return a connection whose body was never read, so an
        // error response is drained before the call ends or the backoff starts.
        await discardBody(response);
        const retryable = RETRYABLE_STATUSES.includes(response.status);
        if (!retryable || attempt === attempts - 1) {
          return { ok: false, reason: 'http_status', status: response.status };
        }
        retryDelayMs = backoffDelayMs(response, attempt, backoffMs);
      } else {
        let payload: unknown;
        try {
          payload = await response.json();
        } catch (err) {
          // Three different things reject here, and only one of them is the
          // malformed body this arm is named for. A 2xx header commits nothing
          // about the body that follows, so the cause has to be read from the
          // rejection:
          //   - this attempt's own abort — the provider stalled (`timeout`);
          //   - a SyntaxError — the bytes arrived and are not JSON, which is the
          //     one genuine content problem;
          //   - anything else — the read FAILED (the peer reset the connection
          //     after committing the headers, a proxy truncated the stream).
          //     That is a transport fault that happens to have a status, not
          //     content, and a caller mapping these onto provider errors has to
          //     see it as one.
          await discardBody(response);
          return {
            ok: false,
            reason: bodyFailureReason(err, controller.signal),
            status: response.status,
          };
        }

        const parsed = decisionsResponseSchema.safeParse(payload);
        if (!parsed.success) {
          return { ok: false, reason: 'unexpected_shape', status: response.status };
        }

        return {
          ok: true,
          answers: parsed.data.answers,
          model: parsed.data.model ?? null,
          provider: parsed.data.provider ?? null,
          id: parsed.data.id ?? null,
          usage: {
            input_tokens: parsed.data.usage?.input_tokens ?? null,
            output_tokens: parsed.data.usage?.output_tokens ?? null,
            cost: parsed.data.usage?.cost ?? null,
          },
        };
      }
    } finally {
      clearTimeout(timer);
    }

    // Only a retryable status reaches here, and it always set the delay. The
    // backoff sits outside the attempt so the next one starts its own timeout.
    await sleep(retryDelayMs ?? 0);
  }

  // The loop returns on its last attempt whatever happens, so this is the
  // compiler's exit rather than a reachable one.
  return { ok: false, reason: 'http_status', status: null };
}

function buildPayload(request: DecisionsRequest): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    model: request.model,
    state: request.state,
    questions: request.questions,
  };
  if (request.sessionId) payload.session_id = request.sessionId;
  if (request.user) payload.user = request.user;
  if (request.providerPreferences) payload.provider = request.providerPreferences;
  return payload;
}

/**
 * How long to wait before the next attempt: the server's own `Retry-After` when
 * it names a positive number of seconds, else exponential backoff from
 * `backoffMs`. Both are capped, so a header asking for minutes does not hold a
 * turn open, and a `Retry-After` of `0` or an HTTP-date form falls to the backoff
 * rather than retrying instantly against an endpoint that asked to be given room.
 */
function backoffDelayMs(response: Response, attempt: number, backoffMs: number): number {
  const header = response.headers.get('Retry-After');
  const seconds = header === null ? NaN : Number(header);
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.min(DECISIONS_MAX_BACKOFF_MS, seconds * 1000);
  }
  return Math.min(DECISIONS_MAX_BACKOFF_MS, backoffMs * 2 ** attempt);
}

/**
 * Why a body read rejected: the abort, malformed bytes, or a failed read.
 *
 * `SyntaxError` is what `JSON.parse` raises once the bytes are in hand, so it is
 * the only rejection here that means the provider actually delivered a body this
 * client could not read. A stream that errors — `TypeError`, a network
 * `DOMException` — delivered nothing, whatever status its headers carried.
 */
function bodyFailureReason(err: unknown, signal: AbortSignal): DecisionsFailureReason {
  if (signal.aborted) return 'timeout';
  return err instanceof SyntaxError ? 'unparseable_body' : 'transport';
}

/** Release the connection an unread body would hold open. */
async function discardBody(response: Response): Promise<void> {
  try {
    await response.body?.cancel();
  } catch {
    /* already consumed or errored — the call's outcome does not depend on this */
  }
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
